import { AfterViewInit, Component, EventEmitter, HostListener, Input, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { buttonInOut } from 'animations-lib';
import { combineLatest, Observable, of } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { IdentityStorage } from 'storage-service-lib';
import { environment } from '../../../environments/environment';
import { EntityTypeLabelPair, IFieldMeta } from '../../interfaces/imetadata';
import { ISignatureFieldEvent } from '../../interfaces/signature';
import { DebugService } from '../../services/debug.service';
import { FieldConfig } from '../../utils/fieldconfig';
import { FieldSelectionFactory } from './field-selection.factory';
import { IReInkable } from './ireinkable';
import { IValidStateChangeProvider, IVisibleStateProvider } from './valid-state-provider';

type IndexedState = { index: number; state: boolean };

@Component({
  selector: 'app-field-selection',
  templateUrl: './field-selection.component.html',
  styleUrls: ['./field-selection.component.scss', './field-config/field-config.component.scss'],
  animations: [buttonInOut],
  standalone: false,
})
export class FieldSelectionComponent implements OnInit, AfterViewInit {
  namespace: string | undefined;

  @Input()
  signatureProviderName: string | undefined;

  @Output()
  fieldInsertedEvent = new EventEmitter<{
    entity: EntityTypeLabelPair;
    field: IFieldMeta;
  }>();

  @Output()
  signatureInsertedEvent = new EventEmitter<ISignatureFieldEvent>();

  @Output()
  fieldUpdatedEvent = new EventEmitter<{
    entity: EntityTypeLabelPair;
    field: IFieldMeta;
    fieldConfig: FieldConfig;
  }>();

  @ViewChild('dataFieldsComponent')
  dataFieldsComponent!: IValidStateChangeProvider & IReInkable;

  @ViewChild('documentSenderComponent')
  documentSenderComponent!: IValidStateChangeProvider;

  @ViewChild('docusignComponent')
  docusignComponent: IValidStateChangeProvider | undefined;

  @ViewChild('scriptFieldsComponent')
  scriptFieldsComponent!: IValidStateChangeProvider;

  @ViewChild('imageFieldsComponent')
  imageFieldsComponent!: IValidStateChangeProvider & IReInkable;

  @ViewChild('conditionalFieldsComponent')
  conditionalFieldsComponent!: IValidStateChangeProvider;

  @ViewChild('aiAssistPromptComponent')
  aiAssistPromptComponent!: IValidStateChangeProvider & IVisibleStateProvider;

  tabValidStates: (boolean | undefined)[] = [undefined, undefined, undefined, true, undefined, undefined, undefined];
  selectedTabIndex = 0;
  showNoActionsMessage = false;
  imageFieldsSupported = true;
  aiAssistTabVisible = true; // if you make this false it will break!
  adcMigrationTabVisible = false;

  protected readonly environment = environment;

  constructor(
    private identityStorage: IdentityStorage,
    private ngZone: NgZone,
    private fieldSelectionFactory: FieldSelectionFactory,
    protected debugService: DebugService
  ) {}

  ngOnInit(): void {
    this.namespace = this.identityStorage.get()?.namespace;
    if (this.namespace) {
      this.imageFieldsSupported = this.fieldSelectionFactory.isImageFieldSupported(this.namespace);
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.aiAssistPromptComponent.visible.subscribe((isVisible) => (this.aiAssistTabVisible = isVisible));
    }, 0);

    const validStateProviders: Observable<IndexedState>[] = this.buildIndexedValidStateProviders();

    combineLatest(validStateProviders)
      .pipe(debounceTime(100))
      .subscribe((tabValidStates) => this.onTabValidStateChange(tabValidStates));
  }

  // Keyboard Shortcut - Use Alt+ M to toggle ADC migration tab
  @HostListener('document:keydown.alt.M')
  onKeyDown() {
    this.adcMigrationTabVisible = !this.adcMigrationTabVisible;
  }

  reInkChildren(): void {
    this.dataFieldsComponent?.reInkMatTab();
    this.imageFieldsComponent?.reInkMatTab();
  }

  onSignatureInserted(event: ISignatureFieldEvent): void {
    this.signatureInsertedEvent.emit(event);
  }

  private onTabValidStateChange(tabValidStates: IndexedState[]): void {
    this.ngZone.run(() => {
      this.tabValidStates = tabValidStates.map((ivs) => ivs.state);
      const firstValidTabIndex = this.tabValidStates.findIndex((v) => v);
      this.selectedTabIndex = firstValidTabIndex > -1 ? tabValidStates[firstValidTabIndex].index : 0;
      this.showNoActionsMessage = firstValidTabIndex === -1;
    });
  }

  private buildIndexedValidStateProviders(): Observable<IndexedState>[] {
    // this design is flawed - if the components are destroyed then these subscriptions are never recreated

    let tabIndex = 0;
    return [
      this.dataFieldsComponent,
      this.documentSenderComponent,
      this.docusignComponent,
      this.scriptFieldsComponent,
      this.imageFieldsComponent,
      this.conditionalFieldsComponent,
      this.aiAssistPromptComponent,
    ].map((validStateProvider) => {
      if (validStateProvider) {
        const finalIndex = tabIndex;
        tabIndex++;
        return validStateProvider.validStateChange$.pipe(map((state) => ({ state: state, index: finalIndex })));
      }

      return of({ state: false, index: -1 });
    });
  }
}
